<template>
  <v-card flat class="controls-bar">
    <v-app-bar elevation="1" class="px-11">
      <v-app-bar-title class="text-primary text-h6 font-weight-bold">
        {{ label }}
      </v-app-bar-title>
      <v-row v-if="!isDigitalTwinRoute && !isEditRoute">
        <v-col>
          <SiteAutocomplete
            class="mt-6 mr-5"
            :items="sitesWithControls"
            :item-title="siteDisplayName"
            v-model="selectedSite"
            @update:modelValue="
              (selectedSite) => {
                clearAnchorPosition();
                setDefaultSiteFromControlsDomain(selectedSite);
              }
            "
          />
        </v-col>
        <v-col>
          <v-text-field
            class="mt-6 mr-5"
            v-model="searchTerm"
            hide-details
            prepend-inner-icon="mdi-magnify"
            bg-color="white"
            single-line
            variant="outlined"
            density="compact"
            :placeholder="placeholder"
            :clearable="true"
            @click:clear="handleSearchInput"
            @update:modelValue="handleSearchInput"
          />
        </v-col>
      </v-row>
      <v-spacer />
    </v-app-bar>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import {
  areSiteNamesRedacted,
  redactedSiteLabel,
  REDACTED_SITE_NAME,
} from '@/configuration/featureFlags';

import SiteAutocomplete from '@/Core.UI.Domain/Components/SiteAutocomplete.vue';

export default {
  name: 'ControlsBar',

  components: { SiteAutocomplete },

  data() {
    return {
      searchTerm: '',
      selectedSite: null,
    };
  },

  async created() {
    await this.loadSitesForRoute();
  },

  beforeUnmount() {
    this.setControlCurrentRoute('');
  },

  computed: {
    ...mapGetters({
      sitesWithControls: 'ControlsStore/sitesWithControls',
      defaultSite: 'session/defaultSite',
    }),

    // In the demo environment real site names must not appear on screen. Keyed
    // by site id (not list position) so a site keeps the same label however the
    // list is ordered or refetched; null when redaction is off.
    redactedSiteLabels() {
      if (!areSiteNamesRedacted()) return null;

      const ids = [...this.sitesWithControls]
        .map((site) => site.id)
        .sort((a, b) => a - b);

      return new Map(ids.map((id, index) => [id, redactedSiteLabel(index)]));
    },

    placeholder() {
      return this.$route.meta.placeholder || 'Search for a Control';
    },

    label() {
      return this.$route.meta.label || 'Controls';
    },

    controlTypeName() {
      return this.$route.meta.controlTypeName;
    },

    controlTypeId() {
      return this.$route.meta.controlTypeId;
    },

    isDigitalTwinRoute() {
      return [
        'DigitalTwin',
        'DigitalTwinMVP',
        'DigitalTwinMVPManagement',
        'DigitalTwinMVPAdminIndex',
      ].includes(this.$route.name);
    },

    isEditRoute() {
      const regex = /edit/i;
      return regex.test(this.$route.path);
    },
  },

  watch: {
    async $route(to) {
      const regex = /\/history\//;
      if (regex.test(to.path)) return;

      await this.loadSitesForRoute();
      this.clearSearchTerm();
    },
  },

  methods: {
    siteDisplayName(site) {
      if (!site) return '';
      if (!this.redactedSiteLabels) return site.name || '';
      return this.redactedSiteLabels.get(site.id) || REDACTED_SITE_NAME;
    },

    ...mapActions({
      getSitesWithControls: 'ControlsStore/getSitesWithControls',
      getControlsBySite: 'ControlsStore/getControlsBySite',
      setControlCurrentRoute: 'ControlsStore/setControlCurrentRoute',
      setDefaultSite: 'session/setDefaultSite',
    }),

    ...mapMutations({
      setControlSearchTerm: 'ControlsStore/CONTROL_SEARCH_TERM',
      setSelectedSiteControls: 'ControlsStore/SELECTED_SITE_CONTROLS',
    }),

    async loadSitesForRoute() {
      // False means the user has already moved to another control page, which
      // asked for its own list: selecting a site is left to that newer call.
      const isLatest = await this.getSitesWithControls();
      if (!isLatest) return;

      this.setDefaultSiteFromControlsDomain(this.defaultSite);
    },

    setDefaultSiteFromControlsDomain(selectedSite) {
      // The remembered site may not offer this page's control type (arriving
      // on AHUs from Power Controls, say): fall back to the first site that
      // does, otherwise the page shows a site its own dropdown does not list.
      // Other pages remember the site keyed by `siteId` rather than `id`, so
      // match on either and keep this list's own entry.
      const selectedSiteId = selectedSite?.siteId ?? selectedSite?.id;
      this.selectedSite =
        this.sitesWithControls.find((site) => site.id === selectedSiteId) ??
        this.sitesWithControls[0];

      // No site offers this control type: the fetch below needs a site, so
      // skip it, drop the controls left over from the previous page, and still
      // flag the route so the page leaves its loader.
      if (!this.selectedSite) {
        this.setSelectedSiteControls([]);
        this.setControlCurrentRoute(this.$route.name);
        return;
      }

      this.getControlsBySite({
        selectedSite: this.selectedSite,
        controlTypeName: this.controlTypeName,
        controlTypeId: this.controlTypeId,
      }).then(() => {
        this.setControlCurrentRoute(this.$route.name);
        this.setDefaultSite(this.selectedSite);
        this.clearSearchTerm();
      });
    },

    handleSearchInput(value) {
      if (typeof value === 'object') {
        this.setControlSearchTerm('');
      } else {
        this.setControlSearchTerm(value);
      }
    },

    clearSearchTerm() {
      this.searchTerm = '';
      this.setControlSearchTerm('');
    },

    clearAnchorPosition() {
      localStorage.removeItem('controlAnchorPosition');
    },
  },
};
</script>
<style lang="scss" scoped>
.controls-bar {
  z-index: 1;
}

.v-input {
  max-width: 700px !important;
}

:deep(.v-field__input) {
  color: $primary !important;
}

:deep(.bg-white) {
  color: $primary !important;
}
</style>
